import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { validateCheckoutForm } from '../lib/checkoutValidation';
import { readSavedDeliveryLocation } from '../lib/deliveryLocation';
import LocationPinIcon from '../components/LocationPinIcon';
import { DEFAULT_PREPARATION_DAYS, formatBusinessDays } from '../lib/preparationTime';
import { NO_REFUND_POLICY_TEXT, NO_REFUND_POLICY_VERSION } from '../lib/storePolicies';

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    let timeoutId;
    const finish = (result) => {
      window.clearTimeout(timeoutId);
      resolve(result);
    };

    if (window.Razorpay) {
      finish(true);
      return;
    }

    const existingScript = document.querySelector('script[data-razorpay-sdk="storefront"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => finish(Boolean(window.Razorpay)), { once: true });
      existingScript.addEventListener('error', () => finish(false), { once: true });
      timeoutId = window.setTimeout(() => finish(false), 12000);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.setAttribute('data-razorpay-sdk', 'storefront');
    script.onload = () => finish(Boolean(window.Razorpay));
    script.onerror = () => finish(false);
    document.body.appendChild(script);
    timeoutId = window.setTimeout(() => finish(false), 12000);
  });

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

const itemPrice = (item) => safeNumber(item.final_price ?? item.price_mrp ?? item.price);
const itemQuantity = (item) => {
  const quantity = Math.floor(safeNumber(item.quantity));
  return quantity > 0 ? quantity : 1;
};
const itemName = (item) => item.title || item.name || 'Handmade item';
const itemVariantId = (item) => item.variant_id ?? item.variantId ?? null;
const getItemDeliveryDetails = (item, quote) => {
  const productId = String(item.productId || item.product_id || item.id);
  const variantId = String(itemVariantId(item) ?? '');
  return quote?.originBreakdown?.find((origin) => (
    origin.items?.some((originItem) => (
      String(originItem.productId || originItem.product_id) === productId
      && String(originItem.variantId ?? originItem.variant_id ?? '') === variantId
    ))
  )) || null;
};
const readJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const createInitialCheckoutForm = (user) => {
  const savedLocation = readSavedDeliveryLocation();
  return {
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    shipping_address: savedLocation?.address || '',
    shipping_city: savedLocation?.city || '',
    shipping_state: savedLocation?.state || '',
    shipping_zip: savedLocation?.zip || '',
    shipping_country: savedLocation?.country || 'India',
  };
};

export default function CheckoutPage() {
  const { cartItems, cartLoading, clearCart } = useContext(CartContext);
  const { user, token, authConfig, refreshAuthConfig } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => createInitialCheckoutForm(user));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('prepaid');
  const [fulfillmentMethod, setFulfillmentMethod] = useState('delivery');
  const [pickupOriginCode, setPickupOriginCode] = useState('');
  const [shippingQuote, setShippingQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [noRefundAccepted, setNoRefundAccepted] = useState(false);

  const itemsInCart = useMemo(() => (Array.isArray(cartItems) ? cartItems : []), [cartItems]);
  const testPaymentAvailable = Boolean(authConfig?.testPaymentConfigured);
  const razorpayAvailable = Boolean(authConfig?.razorpayConfigured);
  const prepaidUnavailable = !razorpayAvailable && !testPaymentAvailable;

  const subtotal = itemsInCart.reduce(
    (sum, item) => sum + itemPrice(item) * itemQuantity(item),
    0
  );

  const promoDiscount = Number(appliedPromo?.discountAmount || 0);
  const shippingCost = Number(shippingQuote?.shippingCost || 0);
  const totalAmount = Math.max(0, subtotal - promoDiscount + shippingCost);
  const partialAdvancePercent = Number(shippingQuote?.partialCodAdvancePercent || 20);
  const partialAdvance = Number((totalAmount * partialAdvancePercent / 100).toFixed(2));
  const shippingInfoReady = Boolean(
    formData.shipping_city.trim() &&
    formData.shipping_state.trim() &&
    /^\d{6}$/.test(formData.shipping_zip.trim()) &&
    formData.shipping_country.trim().toLowerCase() === 'india'
  );
  const quoteItems = useMemo(() => itemsInCart.map((item) => ({
    productId: item.product_id || item.id,
    variantId: itemVariantId(item),
    quantity: itemQuantity(item),
  })), [itemsInCart]);
  const codAvailable = Boolean(shippingQuote?.codAvailable && authConfig?.codConfigured !== false);
  const partialCodAvailable = Boolean(
    shippingQuote?.partialCodAvailable &&
    authConfig?.testPaymentConfigured
  );
  const pickupOptions = Array.isArray(shippingQuote?.pickupOptions) ? shippingQuote.pickupOptions : [];
  const pickupAvailable = Boolean(shippingQuote?.pickupAvailable && pickupOptions.length > 0);
  const paymentUnavailable = paymentMethod === 'prepaid' && prepaidUnavailable;

  useEffect(() => {
    const applySavedLocation = (location) => {
      if (!location) return;
      setFormData((current) => ({
        ...current,
        shipping_address: location.address || current.shipping_address,
        shipping_city: location.city || current.shipping_city,
        shipping_state: location.state || current.shipping_state,
        shipping_zip: location.zip || current.shipping_zip,
        shipping_country: location.country || current.shipping_country,
      }));
    };

    const handleLocationUpdate = (event) => applySavedLocation(event.detail || readSavedDeliveryLocation());
    window.addEventListener('hunar:delivery-location-updated', handleLocationUpdate);
    applySavedLocation(readSavedDeliveryLocation());
    return () => window.removeEventListener('hunar:delivery-location-updated', handleLocationUpdate);
  }, []);

  useEffect(() => {
    setFormData((current) => ({
      ...current,
      customer_name: current.customer_name || user?.name || '',
      customer_email: current.customer_email || user?.email || '',
    }));
  }, [user]);

  useEffect(() => {
    if (testPaymentAvailable && !razorpayAvailable && paymentMethod === 'prepaid') {
      setPaymentMethod('test');
    }
  }, [testPaymentAvailable, razorpayAvailable, paymentMethod]);

  useEffect(() => {
    if (cartLoading || !shippingInfoReady || quoteItems.length === 0) {
      setShippingQuote(null);
      setQuoteError(null);
      setQuoteLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setQuoteLoading(true);
      setQuoteError(null);
      try {
        const response = await fetch('/api/shipping/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            shippingInfo: {
              city: formData.shipping_city,
              state: formData.shipping_state,
              zip: formData.shipping_zip,
              country: formData.shipping_country,
            },
            items: quoteItems,
            paymentMethod,
            fulfillmentMethod,
            pickupOriginCode,
          }),
          signal: controller.signal,
        });
        const data = await readJsonSafely(response);
        if (!response.ok) throw new Error(data.msg || 'Delivery charges are unavailable right now.');
        setShippingQuote(data);
        if (fulfillmentMethod === 'pickup' && !data.pickupAvailable) {
          setFulfillmentMethod('delivery');
          setPickupOriginCode('');
          setQuoteError(data.pickupMessage || 'Pickup is available only when your address is close to a source location.');
        }
        if (paymentMethod === 'cod' && !data.codAvailable) setPaymentMethod('prepaid');
        if (paymentMethod === 'partial_cod' && !data.partialCodAvailable) setPaymentMethod('prepaid');
      } catch (quoteFetchError) {
        if (quoteFetchError.name !== 'AbortError') {
          setShippingQuote(null);
          setQuoteError(quoteFetchError.message || 'Delivery charges are unavailable right now.');
        }
      } finally {
        if (!controller.signal.aborted) setQuoteLoading(false);
      }
    }, 350);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [cartLoading, formData.shipping_city, formData.shipping_state, formData.shipping_zip, formData.shipping_country, paymentMethod, fulfillmentMethod, pickupOriginCode, quoteItems, shippingInfoReady]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (fieldErrors[name]) {
      setFieldErrors((current) => ({ ...current, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const { normalized, errors } = validateCheckoutForm(formData);

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstInvalid = Object.keys(errors)[0];
      window.requestAnimationFrame(() => document.getElementById(`checkout-${firstInvalid.replaceAll('_', '-')}`)?.focus());
      return null;
    }

    return normalized;
  };

  const handleCheckPayment = async () => {
    setCheckingPayment(true);
    try {
      await refreshAuthConfig();
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleApplyPromo = async () => {
    const normalizedCode = promoCode.trim();
    if (!normalizedCode) {
      setPromoError('Enter a promo code first.');
      setAppliedPromo(null);
      return;
    }
    if (!token) {
      setPromoError('Please sign in before applying a promo code.');
      return;
    }

    setPromoLoading(true);
    setPromoError('');
    try {
      const response = await fetch('/api/promos/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({ promoCode: normalizedCode, items: quoteItems }),
      });
      const data = await readJsonSafely(response);
      if (!response.ok) throw new Error(data.msg || 'This promo code could not be applied.');
      setPromoCode(data.code || normalizedCode);
      setAppliedPromo(data);
    } catch (promoApplyError) {
      setAppliedPromo(null);
      setPromoError(promoApplyError.message || 'This promo code could not be applied.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    const normalizedFormData = validateForm();
    if (!normalizedFormData) {
      setError('Check the highlighted fields and try again.');
      return;
    }

    if (!noRefundAccepted) {
      setError('Please acknowledge the no-refund policy before placing your order.');
      return;
    }

    setError(null);

    if (!user || !token) {
      setError('Please sign in before placing your order.');
      navigate('/login?redirect=/checkout');
      return;
    }

    if (!shippingQuote || quoteLoading) {
      setError(quoteLoading ? 'Still calculating delivery. Please wait a moment.' : 'Enter your city, state, and 6-digit postal code to calculate delivery.');
      return;
    }
    if (fulfillmentMethod === 'pickup' && !pickupOriginCode) {
      setError('Choose the pickup location for this order.');
      return;
    }

    const selectedPaymentMethod = paymentMethod === 'test' ? 'prepaid' : paymentMethod;
    if (selectedPaymentMethod === 'cod' && !codAvailable) {
      setError('Cash on delivery is not available for this address.');
      return;
    }
    if (selectedPaymentMethod === 'partial_cod' && !partialCodAvailable) {
      setError('Partial COD is available only in the local test-payment pipeline right now.');
      return;
    }
    if ((paymentMethod === 'prepaid' && paymentUnavailable) || (paymentMethod === 'test' && !testPaymentAvailable)) {
      setError('Online payment is temporarily unavailable. Please try again later or contact the shop for help.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const shippingInfo = {
        name: normalizedFormData.customer_name,
        email: normalizedFormData.customer_email,
        phone: normalizedFormData.customer_phone,
        address: normalizedFormData.shipping_address,
        city: normalizedFormData.shipping_city,
        state: normalizedFormData.shipping_state,
        zip: normalizedFormData.shipping_zip,
        country: normalizedFormData.shipping_country,
      };

      const items = quoteItems;

      if (selectedPaymentMethod === 'cod') {
        const codRes = await fetch('/api/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
          body: JSON.stringify({
            customer_name: shippingInfo.name,
            customer_email: shippingInfo.email,
            customer_phone: shippingInfo.phone,
            shipping_address: shippingInfo.address,
            shipping_city: shippingInfo.city,
            shipping_state: shippingInfo.state,
            shipping_zip: shippingInfo.zip,
            shipping_country: shippingInfo.country,
            payment_method: 'cod',
            fulfillment_method: fulfillmentMethod,
            pickup_origin_code: pickupOriginCode,
            items: items.map((item) => ({
              product_id: item.productId,
              variant_id: item.variantId,
              quantity: item.quantity,
            })),
            promo_code: appliedPromo?.code || promoCode.trim(),
            no_refund_acknowledged: noRefundAccepted,
            refund_policy_version: NO_REFUND_POLICY_VERSION,
          }),
        });
        const codData = await readJsonSafely(codRes);
        if (!codRes.ok) throw new Error(codData.msg || 'Unable to place the COD order.');
        await clearCart();
        setLoading(false);
        navigate('/orders');
        return;
      }

      if (paymentMethod === 'test' || selectedPaymentMethod === 'partial_cod') {
        const testRes = await fetch('/api/test-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
          body: JSON.stringify({
            shippingInfo,
            items,
            paymentMethod: selectedPaymentMethod,
            fulfillmentMethod,
            pickupOriginCode,
            promoCode: appliedPromo?.code || promoCode.trim(),
            noRefundAcknowledged: noRefundAccepted,
            refundPolicyVersion: NO_REFUND_POLICY_VERSION,
          }),
        });
        const testData = await readJsonSafely(testRes);
        if (!testRes.ok) throw new Error(testData.msg || 'Test payment could not be completed.');
        await clearCart();
        setLoading(false);
        navigate('/orders');
        return;
      }

      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) throw new Error('Razorpay checkout could not be loaded. Check your internet connection and try again.');

      const res = await fetch('/api/razorpay/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
        body: JSON.stringify({
          shippingInfo,
          items,
          paymentMethod: selectedPaymentMethod,
          fulfillmentMethod,
          pickupOriginCode,
          promoCode: appliedPromo?.code || promoCode.trim(),
          noRefundAcknowledged: noRefundAccepted,
          refundPolicyVersion: NO_REFUND_POLICY_VERSION,
        }),
      });
      const data = await readJsonSafely(res);
      if (!res.ok) {
        throw new Error(data.msg || 'Unable to start Razorpay checkout.');
      }

      const razorpay = new window.Razorpay({
        key: data.keyId,
        amount: data.amount,
        currency: data.currency || 'INR',
        name: 'Hunar',
        description: `Handmade order for ₹${Number(data.inrTotal || totalAmount).toFixed(2)}`,
        order_id: data.orderId,
        prefill: {
          name: normalizedFormData.customer_name,
          email: normalizedFormData.customer_email,
          contact: normalizedFormData.customer_phone,
        },
        notes: {
          shipping_city: formData.shipping_city,
          shipping_state: formData.shipping_state,
        },
        theme: { color: '#966F23' },
        handler: async (response) => {
          try {
            const verifyRes = await fetch('/api/razorpay/verify-payment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-auth-token': token,
              },
              body: JSON.stringify({
                ...response,
                shippingInfo,
                items,
                paymentMethod: selectedPaymentMethod,
                fulfillmentMethod,
                pickupOriginCode,
                promoCode: appliedPromo?.code || promoCode.trim(),
                noRefundAcknowledged: noRefundAccepted,
                refundPolicyVersion: NO_REFUND_POLICY_VERSION,
              }),
            });
            const verifyData = await readJsonSafely(verifyRes);
            if (!verifyRes.ok) {
              if (verifyRes.status === 401 || verifyRes.status === 403) {
                throw new Error('Your session expired before payment could be verified. Sign in again, then contact the shop with your Razorpay payment ID if you were charged.');
              }
              throw new Error(verifyData.msg || 'Payment verification failed.');
            }
            await clearCart();
            navigate('/orders');
          } catch (verifyError) {
            setError(verifyError.message || 'Your payment could not be verified. Please contact support.');
          } finally {
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });

      razorpay.on('payment.failed', (response) => {
        setError(response.error?.description || 'Payment failed. No order was placed.');
        setLoading(false);
      });
      razorpay.open();
    } catch (err) {
      setError(err.message || 'Unable to start secure checkout.');
      setLoading(false);
    }
  };

  if (cartLoading) {
    return (
      <div className="container py-5 text-center" role="status" aria-live="polite">
        Loading your cart…
      </div>
    );
  }

  if (itemsInCart.length === 0) {
    return (
      <div className="container py-5 text-center">
        <h5>Your cart is empty</h5>
        <button className="btn btn-primary-custom mt-2" onClick={() => navigate('/shop')}>
          Return to Shop
        </button>
      </div>
    );
  }

  return (
    <div className="container checkout-page py-4 py-lg-5">
      <header className="checkout-header">
        <div>
          <h1 className="brand-font">Secure checkout</h1>
          <p>Enter your delivery details to see the charges from each product studio, then choose how to pay.</p>
        </div>
        <p className="checkout-header__shipping">
          Delivery charges from each product source
        </p>
      </header>

      {error && <div className="alert alert-danger checkout-alert py-2 mb-4" role="alert">{error}</div>}

      <div className="row g-4 checkout-layout">
        {/* Shipping Form */}
        <div className="col-12 col-lg-7">
          <form onSubmit={handleSubmitOrder} noValidate className="checkout-panel checkout-form">
            <section className="checkout-section" aria-labelledby="checkout-customer-heading">
            <h2 id="checkout-customer-heading" className="checkout-section__heading"><span>1</span> Customer information</h2>
            <div className="row g-3 mb-4">
              <div className="col-12 col-sm-6">
                <label className="form-label small font-weight-bold" htmlFor="checkout-customer-name">Full Name *</label>
                <input
                  id="checkout-customer-name"
                  type="text"
                  name="customer_name"
                  className="form-control"
                  required
                  autoComplete="name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  aria-invalid={Boolean(fieldErrors.customer_name)}
                  aria-describedby={fieldErrors.customer_name ? 'checkout-customer-name-error' : undefined}
                />
                {fieldErrors.customer_name && <p id="checkout-customer-name-error" className="checkout-field-error">{fieldErrors.customer_name}</p>}
              </div>
              <div className="col-12 col-sm-6">
                <label className="form-label small font-weight-bold" htmlFor="checkout-customer-email">Email Address *</label>
                <input
                  id="checkout-customer-email"
                  type="email"
                  name="customer_email"
                  className="form-control"
                  required
                  autoComplete="email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  aria-invalid={Boolean(fieldErrors.customer_email)}
                  aria-describedby={fieldErrors.customer_email ? 'checkout-customer-email-error' : undefined}
                />
                {fieldErrors.customer_email && <p id="checkout-customer-email-error" className="checkout-field-error">{fieldErrors.customer_email}</p>}
              </div>
              <div className="col-12">
                <label className="form-label small font-weight-bold" htmlFor="checkout-customer-phone">Phone Number</label>
                <input
                  id="checkout-customer-phone"
                  type="tel"
                  name="customer_phone"
                  className="form-control"
                  placeholder="+91 98765 43210"
                  autoComplete="tel"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  aria-invalid={Boolean(fieldErrors.customer_phone)}
                  aria-describedby={fieldErrors.customer_phone ? 'checkout-customer-phone-error' : undefined}
                />
                {fieldErrors.customer_phone && <p id="checkout-customer-phone-error" className="checkout-field-error">{fieldErrors.customer_phone}</p>}
              </div>
            </div>
            </section>

            <section className="checkout-section" aria-labelledby="checkout-shipping-heading">
            <h2 id="checkout-shipping-heading" className="checkout-section__heading"><span>2</span> Shipping address</h2>
            <div className="row g-3 mb-4">
              <div className="col-12">
                <label className="form-label small font-weight-bold" htmlFor="checkout-shipping-address">Street Address *</label>
                <textarea
                  id="checkout-shipping-address"
                  name="shipping_address"
                  className="form-control"
                  rows="2"
                  required
                  autoComplete="street-address"
                  value={formData.shipping_address}
                  onChange={handleChange}
                  aria-invalid={Boolean(fieldErrors.shipping_address)}
                  aria-describedby={fieldErrors.shipping_address ? 'checkout-shipping-address-error' : undefined}
                ></textarea>
                {fieldErrors.shipping_address && <p id="checkout-shipping-address-error" className="checkout-field-error">{fieldErrors.shipping_address}</p>}
              </div>
              <div className="col-12 col-sm-4">
                <label className="form-label small font-weight-bold" htmlFor="checkout-shipping-city">City *</label>
                <input
                  id="checkout-shipping-city"
                  type="text"
                  name="shipping_city"
                  className="form-control"
                  required
                  autoComplete="address-level2"
                  value={formData.shipping_city}
                  onChange={handleChange}
                  aria-invalid={Boolean(fieldErrors.shipping_city)}
                  aria-describedby={fieldErrors.shipping_city ? 'checkout-shipping-city-error' : undefined}
                />
                {fieldErrors.shipping_city && <p id="checkout-shipping-city-error" className="checkout-field-error">{fieldErrors.shipping_city}</p>}
              </div>
              <div className="col-12 col-sm-4">
                <label className="form-label small font-weight-bold" htmlFor="checkout-shipping-state">State *</label>
                <input
                  id="checkout-shipping-state"
                  type="text"
                  name="shipping_state"
                  className="form-control"
                  required
                  autoComplete="address-level1"
                  value={formData.shipping_state}
                  onChange={handleChange}
                  aria-invalid={Boolean(fieldErrors.shipping_state)}
                  aria-describedby={fieldErrors.shipping_state ? 'checkout-shipping-state-error' : undefined}
                />
                {fieldErrors.shipping_state && <p id="checkout-shipping-state-error" className="checkout-field-error">{fieldErrors.shipping_state}</p>}
              </div>
              <div className="col-12 col-sm-4">
                <label className="form-label small font-weight-bold" htmlFor="checkout-shipping-zip">ZIP Code *</label>
                <input
                  id="checkout-shipping-zip"
                  type="text"
                  name="shipping_zip"
                  className="form-control"
                  required
                  autoComplete="postal-code"
                  value={formData.shipping_zip}
                  onChange={handleChange}
                  aria-invalid={Boolean(fieldErrors.shipping_zip)}
                  aria-describedby={fieldErrors.shipping_zip ? 'checkout-shipping-zip-error' : undefined}
                />
                {fieldErrors.shipping_zip && <p id="checkout-shipping-zip-error" className="checkout-field-error">{fieldErrors.shipping_zip}</p>}
              </div>
              <div className="col-12">
                <label className="form-label small font-weight-bold" htmlFor="checkout-shipping-country">Country *</label>
                <input
                  id="checkout-shipping-country"
                  type="text"
                  name="shipping_country"
                  className="form-control"
                  required
                  autoComplete="country-name"
                  value={formData.shipping_country}
                  onChange={handleChange}
                  aria-invalid={Boolean(fieldErrors.shipping_country)}
                  aria-describedby={fieldErrors.shipping_country ? 'checkout-shipping-country-error' : undefined}
                />
                {fieldErrors.shipping_country && <p id="checkout-shipping-country-error" className="checkout-field-error">{fieldErrors.shipping_country}</p>}
              </div>
            </div>
            </section>

            <section className="checkout-section" aria-labelledby="checkout-fulfillment-heading">
            <h2 id="checkout-fulfillment-heading" className="checkout-section__heading"><span>3</span> Delivery or collection</h2>
            <div className="checkout-payment-method">
              <label className="checkout-option" htmlFor="deliveryFulfillment">
                <input
                  className="form-check-input"
                  type="radio"
                  name="fulfillment_method"
                  id="deliveryFulfillment"
                  value="delivery"
                  checked={fulfillmentMethod === 'delivery'}
                  onChange={() => { setFulfillmentMethod('delivery'); setPickupOriginCode(''); }}
                />
                <span><strong>Home delivery</strong><small>Ship from the product source location using the calculated carrier rate.</small></span>
              </label>
              <label className="checkout-option" htmlFor="pickupFulfillment">
                <input
                  className="form-check-input"
                  type="radio"
                  name="fulfillment_method"
                  id="pickupFulfillment"
                  value="pickup"
                  checked={fulfillmentMethod === 'pickup'}
                  disabled={!pickupAvailable}
                  onChange={() => {
                    setFulfillmentMethod('pickup');
                    setPickupOriginCode(pickupOptions[0]?.originCode || '');
                  }}
                />
                <span><strong>Collect it yourself</strong><small>{pickupAvailable ? 'Your address is close enough to a source location for collection.' : quoteError ? `Collection options unavailable: ${quoteError}` : shippingInfoReady ? 'Collection is unavailable for this address.' : 'Enter a nearby city and pincode to see collection options.'}</small></span>
              </label>
              {fulfillmentMethod === 'pickup' && pickupAvailable && (
                <div className="checkout-pickup-options" role="radiogroup" aria-label="Pickup location">
                  {pickupOptions.map((option) => (
                    <label className="checkout-option checkout-option--nested" htmlFor={`pickup-${option.originCode}`} key={option.originCode}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="pickup_origin_code"
                        id={`pickup-${option.originCode}`}
                        value={option.originCode}
                        checked={pickupOriginCode === option.originCode}
                        onChange={() => setPickupOriginCode(option.originCode)}
                      />
                      <span><strong>{option.originLabel}</strong><small>{option.address}<br />{option.originCity}, {option.originState} · Pincode {option.pickupPincode}</small></span>
                    </label>
                  ))}
                </div>
              )}
              {shippingQuote?.partialPickup?.available && (
                <div className="checkout-partial-pickup" role="status">
                  <strong>{shippingQuote.fulfillmentMethod === 'pickup' ? 'This order combines collection and delivery.' : 'Part of this order can be collected.'}</strong>
                  <span>{shippingQuote.fulfillmentMethod === 'pickup' ? 'The selected source will be ready for collection; the remaining items will be delivered to your address.' : 'Choose collection here to collect the nearby items and have the remaining items delivered, or order them separately.'}</span>
                  <span><b>Collect:</b> {shippingQuote.partialPickup.collectableItems.map((item) => itemName(item)).join(', ')}</span>
                  <span><b>Delivery:</b> {shippingQuote.partialPickup.deliveryItems.map((item) => itemName(item)).join(', ')}</span>
                </div>
              )}
            </div>
            </section>

            <section className="checkout-section checkout-section--payment" aria-labelledby="checkout-payment-heading">
            <h2 id="checkout-payment-heading" className="checkout-section__heading"><span>4</span> Payment</h2>
            <div className="checkout-payment-method">
              <label className="checkout-option" htmlFor="razorpayPayment">
                <input
                  className="form-check-input"
                  type="radio"
                  name="payment_method"
                  id="razorpayPayment"
                  value="prepaid"
                  checked={paymentMethod === 'prepaid'}
                  onChange={() => setPaymentMethod('prepaid')}
                  disabled={prepaidUnavailable}
                />
                <span>
                  <strong>{testPaymentAvailable && !razorpayAvailable ? 'Test payment (no charge)' : 'Secure payment with Razorpay'}</strong>
                  <small>{testPaymentAvailable && !razorpayAvailable ? 'Local test mode. Creates a test order without moving money.' : 'Pay with UPI, card, netbanking, or wallet.'}</small>
                </span>
              </label>
              {testPaymentAvailable && razorpayAvailable && (
                <label className="checkout-option" htmlFor="testPayment">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment_method"
                    id="testPayment"
                    value="test"
                    checked={paymentMethod === 'test'}
                    onChange={() => setPaymentMethod('test')}
                  />
                  <span><strong>Test payment (no charge)</strong><small>Use the local test pipeline while validating checkout.</small></span>
                </label>
              )}
              {codAvailable && (
                <label className="checkout-option" htmlFor="codPayment">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment_method"
                    id="codPayment"
                    value="cod"
                    checked={paymentMethod === 'cod'}
                    onChange={() => setPaymentMethod('cod')}
                  />
                  <span><strong>Cash on delivery</strong><small>Pay ₹{totalAmount.toFixed(2)} when the parcel arrives.</small></span>
                </label>
              )}
              {partialCodAvailable && (
                <label className="checkout-option" htmlFor="partialCodPayment">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="payment_method"
                    id="partialCodPayment"
                    value="partial_cod"
                    checked={paymentMethod === 'partial_cod'}
                    onChange={() => setPaymentMethod('partial_cod')}
                  />
                  <span><strong>Partial COD</strong><small>Pay ₹{partialAdvance.toFixed(2)} now; ₹{(totalAmount - partialAdvance).toFixed(2)} on delivery.</small></span>
                </label>
              )}
              {paymentUnavailable && (
                <div className="checkout-payment-unavailable" role="status">
                  <strong>Online payment is temporarily unavailable.</strong>
                  <span>COD may still be available after you enter a serviceable address.</span>
                  <button type="button" className="btn btn-link checkout-payment-retry" onClick={handleCheckPayment} disabled={checkingPayment}>
                    {checkingPayment ? 'Checking payment status…' : 'Check again'}
                  </button>
                </div>
              )}
            </div>
            </section>

            <section className="checkout-section checkout-section--promo" aria-labelledby="checkout-promo-heading">
              <h2 id="checkout-promo-heading" className="checkout-section__heading"><span>5</span> Promo code</h2>
              <p className="small text-muted">New to Hunar? Try <strong>WELCOME10</strong> for 10% off your first order.</p>
              <div className="input-group">
                <label className="visually-hidden" htmlFor="checkout-promo-code">Promo code</label>
                <input
                  id="checkout-promo-code"
                  type="text"
                  className="form-control"
                  value={promoCode}
                  onChange={(event) => {
                    setPromoCode(event.target.value.toUpperCase());
                    setAppliedPromo(null);
                    setPromoError('');
                  }}
                  placeholder="Enter promo code"
                  maxLength={50}
                  autoComplete="off"
                />
                <button type="button" className="btn btn-outline-custom" onClick={handleApplyPromo} disabled={promoLoading || !promoCode.trim()}>
                  {promoLoading ? 'Checking…' : appliedPromo ? 'Applied' : 'Apply'}
                </button>
              </div>
              {appliedPromo && <p className="checkout-promo-success small mb-0 mt-2">{appliedPromo.code} applied — you save ₹{Number(appliedPromo.discountAmount || 0).toFixed(2)}.</p>}
              {promoError && <p className="checkout-field-error mb-0 mt-2" role="alert">{promoError}</p>}
            </section>

            <label className="checkout-policy-check mt-3">
              <input
                type="checkbox"
                checked={noRefundAccepted}
                onChange={(event) => setNoRefundAccepted(event.target.checked)}
              />
              <span>I understand and accept: {NO_REFUND_POLICY_TEXT}</span>
            </label>
            <button
              type="submit"
              disabled={loading || paymentUnavailable || quoteLoading || !shippingQuote || !noRefundAccepted}
              className="btn btn-lg btn-primary-custom checkout-submit w-100 font-weight-bold mt-3"
            >
              {loading ? (paymentMethod === 'cod' ? 'Placing order…' : 'Opening secure payment…') :
                quoteLoading ? 'Calculating delivery…' :
                !shippingQuote ? 'Enter address for delivery charges' :
                paymentMethod === 'cod' ? `${fulfillmentMethod === 'pickup' ? 'Place collection' : 'Place COD'} order (₹${totalAmount.toFixed(2)})` :
                paymentMethod === 'partial_cod' ? `Pay ₹${partialAdvance.toFixed(2)} advance` :
              `Pay securely (₹${totalAmount.toFixed(2)})`}
            </button>
            <p className="checkout-support-links mb-0 mt-3 small text-center">
              Questions before paying? <Link to="/shipping">Shipping</Link>, <Link to="/returns">returns</Link>, and <Link to="/help">order help</Link>.
            </p>
          </form>
        </div>

        {/* Order Summary */}
        <div className="col-12 col-lg-5">
          <aside className="checkout-panel checkout-summary">
            <h2 className="checkout-summary__heading">Your order <span>({itemsInCart.length})</span></h2>

            <div className="checkout-summary__items">
              {itemsInCart.map((item, idx) => (
                <div key={`${item.product_id || item.id}-${itemVariantId(item) ?? 'base'}-${idx}`} className="checkout-order-item">
                  <img
                    src={
                      item.cover_image ||
                      item.images?.[0] ||
                      'https://images.unsplash.com/photo-1584992236310-6edddc08acff?auto=format&fit=crop&w=800&q=80'
                    }
                    alt=""
                    className="checkout-order-item__image"
                  />
                  <div className="checkout-order-item__details">
                    <span className="checkout-order-item__title">{itemName(item)}</span>
                    {item.variant_name && <span className="checkout-order-item__variant">Size: {item.variant_name}</span>}
                    <span className="checkout-order-item__quantity">Qty: {itemQuantity(item)}</span>
                    {(() => {
                      const delivery = getItemDeliveryDetails(item, shippingQuote);
                      const preparationDays = delivery?.preparationDays ?? item.preparation_days ?? DEFAULT_PREPARATION_DAYS;
                      return (
                        <span className="checkout-item-delivery-line">
                          Preparation: {formatBusinessDays(preparationDays)} · Delivery after dispatch: {delivery?.estimatedDays || (shippingInfoReady ? 'Calculating…' : 'Set location')}
                          {delivery?.originCity && (
                            <span className="cart-item-origin-tooltip" tabIndex="0" aria-label={`Ships from ${delivery.originCity}, ${delivery.originState || ''}`.replace(/, $/, '')}>
                              <LocationPinIcon className="cart-item-origin-icon" />
                              <span className="cart-item-origin-tooltip__bubble" role="tooltip">
                                From {delivery.originCity}{delivery.originState ? `, ${delivery.originState}` : ''}
                              </span>
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                  <span className="checkout-order-item__price">
                    ₹{(itemPrice(item) * itemQuantity(item)).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <div className="checkout-summary__line">
              <span>Items subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            {promoDiscount > 0 && (
              <div className="checkout-summary__line text-success">
                <span>Promo{appliedPromo?.code ? ` (${appliedPromo.code})` : ''}</span>
                <span>−₹{promoDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="checkout-summary__line">
              <span>Shipping</span>
              <span>{!shippingQuote ? 'Enter address' : shippingCost === 0 ? <span className="text-success">FREE</span> : `₹${shippingCost.toFixed(2)}`}</span>
            </div>

            {quoteError && <p className="checkout-summary__note text-danger">{quoteError}</p>}
            {shippingQuote && (
              <div className="checkout-delivery-quote" role="status">
                <strong>{shippingQuote.mixedFulfillment
                  ? `Collect from ${shippingQuote.pickup?.originLabel || 'the selected source'} + delivery for the other items`
                  : shippingQuote.fulfillmentMethod === 'pickup'
                    ? `Collect from ${shippingQuote.pickup?.originLabel || 'the selected source'}`
                    : shippingQuote.source || 'Delivery charges'}</strong>
                <span>Preparation: {formatBusinessDays(shippingQuote.preparationDays ?? DEFAULT_PREPARATION_DAYS)} · Delivery after dispatch: {shippingQuote.estimatedDays}</span>
                {shippingQuote.originBreakdown.map((origin) => (
                  <span key={origin.originCode}>
                    {origin.originLabel}: {origin.zone === 'pickup' ? 'collection' : origin.shippingCost === 0 ? 'free' : `₹${origin.shippingCost}`}
                    {origin.preparationDays !== undefined ? ` · Ready in ${formatBusinessDays(origin.preparationDays)}` : ''}
                    {origin.estimatedDays ? ` · ${origin.estimatedDays}` : ''}
                    {origin.carrierName ? ` · ${origin.carrierName}` : ''}
                  </span>
                ))}
              </div>
            )}

            <div className="checkout-summary__total">
              <span>Total</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>

            <p className="checkout-summary__note">
              {shippingQuote?.fulfillmentMethod === 'pickup'
                ? shippingQuote.mixedFulfillment
                  ? 'The selected nearby items will be collected from the source location after confirmation; shipping is charged only for the remaining delivered items.'
                  : 'Collection is available from the selected source location after your order is confirmed.'
                : shippingQuote?.live
                ? 'Live rates and COD availability are checked with the carrier for this address and these source locations.'
                : 'Delivery is estimated from each product source. COD availability is checked against the address and selected items.'}
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}
